import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { CaseAssigneePill } from "src/cases/common/CaseAssigneePill";
import { CaseAssigneeSelect } from "src/cases/common/CaseAssigneeSelect";
import { ClaimCaseButton } from "src/cases/common/ClaimCaseAction";
import {
  TemplateStatus,
  useAssignCase,
  useIsQueueMember,
  useTemplateStatus,
} from "src/cases/hooks";
import { useQueueMembers } from "src/cases/queues/hooks";
import { UserNotFoundPill } from "src/entities/Assignees";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useCurrentUserId } from "src/store/AuthStore";

type CaseAssigneeProps = {
  assignee?: string;
  caseId: string;
  templateId: string;
  queueId: string | undefined;
  etag: number;
  templateVersionId: string;
  templateSchemaVersion: number;
};

export const CaseAssignee: React.FC<CaseAssigneeProps> = ({
  assignee,
  caseId,
  queueId,
  templateId,
  templateVersionId,
  etag,
  templateSchemaVersion,
}) => {
  const { data: members, isLoading: isLoadingMembers } =
    useQueueMembers(queueId);
  const currentUserId = useCurrentUserId();
  const { status } = useTemplateStatus({
    templateId,
    queueId,
    versionId: templateVersionId,
    schemaVersion: templateSchemaVersion,
  });
  const capabilities = useCapabilities();
  const { onAssign: assignCase, isPending } = useAssignCase(queueId);
  const isQueueMember = useIsQueueMember({
    userId: currentUserId,
    queueId: queueId,
  });

  const isOutdatedTemplate = status === TemplateStatus.Outdated;

  const onAssign = async (userId: Nullable<string>) => {
    if (isOutdatedTemplate) return;

    return assignCase({
      caseId,
      etag,
      assigneeId: userId,
    });
  };

  if (isLoadingMembers) {
    return <SkeletonPlaceholder height="h-5.5" width="w-40" />;
  }

  if (capabilities.cases.canManage) {
    return (
      <CaseAssigneeSelect
        disabled={isOutdatedTemplate || isPending || isLoadingMembers}
        users={members}
        value={assignee}
        onChange={onAssign}
      />
    );
  }

  if (!assignee) {
    return (
      <ClaimCaseButton
        disabled={isOutdatedTemplate || !isQueueMember}
        loading={isPending}
        onClick={onAssign}
      />
    );
  }

  const user = members.find((user) => user.id === assignee);
  if (!user) return <UserNotFoundPill id={assignee} />;

  return <CaseAssigneePill assignee={user} />;
};
